// src/pages/AdCreatives.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateAdCreativeModal from "../components/GenerateAdCreativeModal";
import { Plus, Download, Trash2, X, ExternalLink, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 12;
const FEATURE = "ad-creative";
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

/* ----------------------------------------------------------------------------
   PG array / json normalization helpers
----------------------------------------------------------------------------- */
function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    const t = val.trim();
    if (!t || t === "{}") return [];
    if (t.startsWith("{") && t.endsWith("}")) {
      return t
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeMeta(meta) {
  if (!meta) return {};
  if (typeof meta === "object") return meta;
  if (typeof meta === "string") {
    try {
      const parsed = JSON.parse(meta);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

export default function AdCreatives() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const projectId = selectedProduct?.id || paramProjectId;

  // ✅ Match ProductAvatarImages pattern: keep the raw project assets rows
  const [assets, setAssets] = useState([]);
  const [projectAssets, setProjectAssets] = useState([]);

  // ✅ infinite scroll (match ObjectRemover pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);

  const loaderRef = useRef(null);
  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);

  // Quota (images)
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,

    // gating flags from backend
    blocked: false,
    payment_blocked: false,
    can_use: true,

    period_start: null,
    period_end: null,

    // optional extras (may exist)
    sub_status: null,
    tier: null,
    subscription_active: null,
  });

  // ✅ match ObjectRemover: stable recalcHasMore(arr, pageNum)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // ✅ keep pageRef in sync
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // --------------------------- Quota fetcher ---------------------------
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(`${BACKEND_URL}/api/adcreatives/quota`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  // Fetch project (images/videos for modal + assets list for grid)
  const fetchProject = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
      if (!projectId) {
        setProjectAssets([]);
        // keep local temps only (if any)
        setAssets((prev) =>
          (prev || []).filter((a) => String(a?.id || "").startsWith("tmp-"))
        );
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) {
        setInitialLoading(true);
        // ✅ reset pagination on full fetch
        setPage(1);
        pageRef.current = 1;
      }

      try {
        const token = await getToken();
        const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const imgs = toArray(res.data.images);
        const vids = toArray(res.data.videos);
        setProjectAssets([...imgs, ...vids]);

        // ✅ IMPORTANT (fixes your "3 pendings" issue):
        // Replace with server assets on fetch, do NOT preserve multiple local tmp tiles.
        // This matches ProductAvatarImages behavior and prevents phantom pendings.
        const serverAssets = Array.isArray(res.data.assets)
          ? res.data.assets
          : Array.isArray(res.data?.assetsByFeature?.[FEATURE])
          ? res.data.assetsByFeature[FEATURE]
          : [];

        setAssets(serverAssets);

        setError(null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Fetch project failed", e?.response?.data || e.message);
        setError(
          e?.response?.status === 401
            ? "Session expired. Please sign in again."
            : "Failed to fetch ad creatives."
        );
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId]
  );

  // Initial load + when project changes
  useEffect(() => {
    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    setAssets([]);
    void fetchProject(false);
    void fetchQuota();
  }, [fetchProject, fetchQuota, projectId]);

  // ✅ Feature filtering (match ProductAvatarImages)
  const featureAssets = useMemo(() => {
    return (assets || [])
      .filter((a) => {
        const meta = normalizeMeta(a?.metadata);
        const feature = String(meta.feature || "").toLowerCase();
        if (feature !== FEATURE) return false;

        const st = String(meta.status || "").toLowerCase();
        const isDeleted = meta.deleted === true || st === "deleted";
        return !isDeleted;
      })
      .sort(
        (l, r) =>
          new Date(r.updated_at || r.created_at) -
          new Date(l.updated_at || l.created_at)
      );
  }, [assets]);

  // ✅ page-based slicing (match ObjectRemover)
  const pagedAssets = useMemo(
    () => featureAssets.slice(0, page * PAGE_SIZE),
    [featureAssets, page]
  );

  // ✅ keep hasMore in sync when page/list changes
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < featureAssets.length);
  }, [page, featureAssets.length]);

  // ✅ polling (match ProductAvatarImages; no stale closure)
  const hasPending = useMemo(() => {
    return featureAssets.some((a) => {
      const meta = normalizeMeta(a?.metadata);
      const st = String(meta.status || "").toLowerCase();
      if (st === "failed") return false; // failed is not pending
      if (st === "finished") return false;

      const hasPlaceholderFrag =
        typeof a?.url === "string" && String(a.url).includes("#");
      const noUrl = !a?.url;

      // treat any non-finished/non-failed status OR missing url as pending-ish
      return (
        st === "pending" ||
        st === "queued" ||
        st === "processing" ||
        noUrl ||
        hasPlaceholderFrag
      );
    });
  }, [featureAssets]);

  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (hasPending && projectId) {
      pollTimerRef.current = setInterval(() => {
        fetchProject(true);
      }, 3000);
    } else {
      void fetchQuota();
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [hasPending, projectId, fetchProject, fetchQuota]);

  // ✅ match ObjectRemover: loadMore increments page
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return undefined;
    const el = loaderRef.current;
    if (!el) return undefined;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  // Force a direct download from Cloudinary (no preview/new tab)
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore
    }
    return url;
  }, []);

  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  const addOptimisticPending = useCallback(() => {
    const tmpId = `tmp-${Date.now()}`;

    // ✅ keep at most ONE local temp tile to avoid "phantom multiple pendings"
    setAssets((prev) => [
      {
        id: tmpId,
        url: "/spinner.gif#pending",
        name: `creative-${tmpId}`,
        type: "photo",
        metadata: { feature: FEATURE, status: "pending", local: true },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      ...(prev || []).filter((a) => !String(a?.id || "").startsWith("tmp-")),
    ]);

    recalcHasMore(featureAssets, pageRef.current);
  }, [recalcHasMore, featureAssets]);

  // Delete creative (SOFT delete on BE, does NOT restore quota)
  const handleDelete = useCallback(
    async (assetId) => {
      if (!assetId) return;

      // local-only optimistic tiles
      if (String(assetId).startsWith("tmp-")) {
        setAssets((prev) => {
          const next = (prev || []).filter((a) => a.id !== assetId);
          recalcHasMore(featureAssets, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this creative?")) return;

      try {
        const token = await getToken();

        await axios.delete(`${BACKEND_URL}/api/adcreatives/delete`, {
          data: { projectId, id: assetId },
          headers: { Authorization: `Bearer ${token}` },
        });

        // Remove locally from UI (soft delete keeps quota counted)
        setAssets((prev) => (prev || []).filter((a) => a.id !== assetId));
        if (preview?.id === assetId) setPreview(null);

        void fetchQuota();
      } catch {
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, projectId, preview, fetchQuota, recalcHasMore, featureAssets]
  );

  const handlePreview = useCallback((asset) => {
    if (asset?.url) setPreview({ url: asset.url, id: asset.id });
  }, []);

  // Preview: close on Esc / click-outside
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  // Route sync with header dropdown
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/ad-creatives/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  // ---- Quota UI helpers ----
  const paymentBlocked = !!quota.payment_blocked;
  const userBlocked = !!quota.blocked;

  // ✅ Match Enhancer: only count tier cap when subscription is active/trialing; add-ons always count.
  const hasActiveSubscription = useMemo(() => {
    const s = String(quota.sub_status || "").toLowerCase();
    const activeLike = s === "active" || s === "trialing";
    return activeLike || quota.subscription_active === true;
  }, [quota.sub_status, quota.subscription_active]);

  const effectiveBaseCap =
    quota.unlimited
      ? quota.baseCap || 0
      : hasActiveSubscription
      ? quota.baseCap || 0
      : 0;

  const totalImages = quota.unlimited
    ? Infinity
    : effectiveBaseCap + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const canGenerate =
    !quota.loading &&
    (quota.unlimited || (quota.remaining || 0) > 0) &&
    !paymentBlocked &&
    !userBlocked &&
    quota.can_use !== false;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Ad Creatives
          </h1>

          {/* ✅ Quota pill (match Enhancer layout: no "inactive subscription" warning UI) */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : userBlocked
                  ? "border-red-500/40 bg-red-500/10"
                  : paymentBlocked
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                userBlocked
                  ? "Account blocked"
                  : paymentBlocked
                  ? "Billing issue"
                  : quota.unlimited
                  ? "Unlimited images this cycle"
                  : "Images usage this cycle"
              }
            >
              {userBlocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Account blocked
                </span>
              ) : paymentBlocked ? (
                <span className="text-amber-300 font-semibold text-sm sm:text-base">
                  Billing issue
                </span>
              ) : quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {totalImages === Infinity ? "∞" : totalImages}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-3">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages === Infinity ? "∞" : totalImages}</span>
            </div>
          </div>
        )}

        <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
          Generate polished static ad creatives using your product assets and AI
          text.
        </p>

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        <div
          className="
            grid gap-4 sm:gap-6
            grid-cols-1
            xs:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
          "
        >
          {/* ✅ Create Card: always visible.
              - No analyzed product → go to Analyze
              - No quota/payment blocked/account blocked → go to Pricing
              - Otherwise open modal
          */}
          <div
            onClick={() => {
              if (!projectId) {
                navigate("/analyze");
                return;
              }
              if (!canGenerate) {
                navigate("/pricing");
                return;
              }
              setModalOpen(true);
            }}
            className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] transition text-center p-4 sm:p-6 ${
              !projectId || !canGenerate
                ? "opacity-60 hover:border-[#3A3A50] hover:text-white cursor-not-allowed"
                : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
            }`}
            title={
              !projectId
                ? "Analyze a product first to start generating"
                : !canGenerate
                ? "Not available. Click to manage plan."
                : "Create a new ad creative"
            }
          >
            <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
            <p className="font-semibold text-base">Create Ad Creative</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              {!projectId
                ? "Analyze your first product to get started"
                : userBlocked
                ? "Your account is blocked."
                : paymentBlocked
                ? "Please fix billing to continue."
                : quota.unlimited
                ? "Unlimited images"
                : `You have ${remaining} left of ${
                    totalImages === Infinity ? "∞" : totalImages
                  } this cycle`}
            </p>

            {!!projectId && !canGenerate && !userBlocked && (
              <p className="text-xs text-red-300 mt-2">
                You’ve reached your image limit or billing needs attention.
              </p>
            )}
            {!!projectId && userBlocked && (
              <p className="text-xs text-red-300 mt-2">
                Contact support to regain access.
              </p>
            )}
          </div>

          {initialLoading ? (
            Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#2A2A40] bg-[#1C1C34] h-[210px] sm:h-[300px] animate-pulse"
              />
            ))
          ) : (
            pagedAssets.map((a) => {
              const meta = normalizeMeta(a?.metadata);
              const st = String(meta.status || "").toLowerCase();

              const isFailed = st === "failed";
              const pendingish =
                st === "pending" || st === "queued" || st === "processing";

              const hasPlaceholderFrag =
                typeof a?.url === "string" && String(a.url).includes("#");
              const noUrl = !a?.url;

              // IMPORTANT: failed is not pending
              const isPending = !isFailed && (pendingish || hasPlaceholderFrag || noUrl);
              const url = isPending ? "/spinner.gif" : a.url;

              return (
                <div
                  key={a.id}
                  className={`relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden ${
                    isFailed ? "border-red-500/40" : "border-[#2A2A40]"
                  }`}
                >
                  {/* Delete always available */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(a.id);
                    }}
                    className="absolute top-2 right-2 bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition z-20"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>

                  {isPending ? (
                    <img
                      src="/spinner.gif"
                      alt="Generating..."
                      className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                      draggable={false}
                      style={{
                        background: "#111124",
                        height: "100%",
                        maxHeight: "calc(100% - 32px)",
                      }}
                    />
                  ) : isFailed ? (
                    <div className="flex flex-col items-center justify-center text-center px-4">
                      <AlertTriangle className="w-7 h-7 mb-2 text-red-300" />
                      <div className="text-sm font-semibold text-white">
                        Generation failed
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Please try again.
                      </div>
                    </div>
                  ) : (
                    <>
                      <img
                        src={url}
                        alt={a?.name || meta?.template_id || "Ad Creative"}
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => handlePreview(a)}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(url);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(a.id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Infinite loader */}
        {hasMore && !initialLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img
                src="/spinner.gif"
                alt="Loading..."
                className="w-7 h-7 mx-auto"
              />
            )}
          </div>
        )}

        {modalOpen && projectId && (
          <GenerateAdCreativeModal
            projectId={projectId}
            projectAssets={projectAssets}
            onClose={() => setModalOpen(false)}
            onCreated={() => {
              // ✅ Optimistic pending tile + optimistic quota decrement
              addOptimisticPending();
              if (!quota.unlimited) {
                setQuota((q) => ({
                  ...q,
                  remaining: Math.max(0, (q.remaining || 0) - 1),
                  used: (q.used || 0) + 1,
                }));
              }
              fetchProject(true);
            }}
          />
        )}

        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>

              {error && (
                <div className="text-red-500 px-6 pb-4 -mt-2">{error}</div>
              )}
            </div>
          </div>
        )}

        {error && !initialLoading && (
          <div className="text-red-500 mt-4">{error}</div>
        )}
      </div>
    </div>
  );
}
